import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  macroRequestsService,
  CreateMacroRequestInput,
} from "../../services/admin/macroRequests.service";

export function usePendingMacroRequests(enabled = true) {
  return useQuery({
    queryKey: ["macro-requests", "pending"],
    queryFn: () => macroRequestsService.getPending(),
    enabled,
  });
}

export function useMyMacroRequests(enabled = true) {
  return useQuery({
    queryKey: ["macro-requests", "mine"],
    queryFn: () => macroRequestsService.getMine(),
    enabled,
  });
}

export function useCreateMacroRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateMacroRequestInput) =>
      macroRequestsService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["macro-requests"] });
      notifications.show({
        title: "Request Submitted",
        message:
          "Your macro request has been submitted for review by an administrator.",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to submit macro request",
        color: "red",
      });
    },
  });
}

export function useApproveMacroRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      macroRequestsService.approve(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["macro-requests"] });
      queryClient.invalidateQueries({ queryKey: ["diagnosis"] });
      queryClient.invalidateQueries({ queryKey: ["diagnosis-macros"] });
      notifications.show({
        title: "Request Approved",
        message:
          "The macro request has been approved and the diagnosis macro has been created.",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to approve macro request",
        color: "red",
      });
    },
  });
}

export function useRejectMacroRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      macroRequestsService.reject(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["macro-requests"] });
      notifications.show({
        title: "Request Rejected",
        message: "The macro request has been rejected.",
        color: "orange",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to reject macro request",
        color: "red",
      });
    },
  });
}
